import React from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Container,
  Divider,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import {
  Business,
  Group,
  Description,
  ArrowForward,
  Add as AddIcon,
  Timeline,
} from '@mui/icons-material';

// Small, reusable stat card with calm styling
interface StatCardProps {
  label: string;
  value: string | number;
  helper?: string;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, helper }) => {
  const theme = useTheme();

  return (
    <Card
      sx={{
        height: '100%',
        borderRadius: 2,
        borderColor: 'divider',
        boxShadow: 'none',
        '&:hover': {
          // Override global hover lift for calmer feel
          transform: 'none',
          boxShadow: 'none',
          borderColor: theme.palette.action.hover,
        },
      }}
    >
      <CardContent>
        <Typography
          variant="overline"
          color="text.secondary"
          sx={{ letterSpacing: 0.6, textTransform: 'uppercase' }}
        >
          {label}
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 600, mt: 0.5 }}>
          {value}
        </Typography>
        {helper && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {helper}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

// Simple empty state pattern
interface EmptyStateProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  actionLabel?: string;
  actionTo?: string;
}

const EmptyState: React.FC<EmptyStateProps> = ({
  icon,
  title,
  description,
  actionLabel,
  actionTo,
}) => (
  <Box sx={{ p: 3, textAlign: 'center' }}>
    <Box sx={{ mb: 2, color: 'text.disabled' }}>{icon}</Box>
    <Typography variant="h6" color="text.primary" gutterBottom>
      {title}
    </Typography>
    <Typography variant="body2" color="text.secondary" paragraph>
      {description}
    </Typography>
    {actionLabel && actionTo && (
      <Button
        component={Link}
        to={actionTo}
        variant="contained"
        startIcon={<AddIcon />}
        sx={{ mt: 1 }}
      >
        {actionLabel}
      </Button>
    )}
  </Box>
);

const DashboardSample: React.FC = () => {
  const theme = useTheme();

  // Mock data purely for layout/visual evaluation
  const stats = [
    { label: 'Organizations', value: 3, helper: 'You manage 2 and belong to 1 more' },
    { label: 'Teams', value: 7, helper: 'Active teams across all organizations' },
    { label: 'Shared Views', value: 24, helper: 'Saved views your teams use frequently' },
    { label: 'Invitations', value: 1, helper: 'Awaiting your response' },
  ];

  const organizations = [
    { id: '1', name: 'Tier One Studios', role: 'Owner', teams: 4 },
    { id: '2', name: 'Acme Marketing', role: 'Admin', teams: 2 },
    { id: '3', name: 'Personal Workspace', role: 'Member', teams: 1 },
  ];

  const teams = [
    { id: 't1', name: 'Studio Core Team', organization: 'Tier One Studios' },
    { id: 't2', name: 'Launch Campaign', organization: 'Acme Marketing' },
    { id: 't3', name: 'Content Review', organization: 'Tier One Studios' },
  ];

  const recentActivity = [
    {
      id: 'a1',
      description: '"Launch Assets" view shared with Studio Core Team',
      context: 'Tier One Studios',
      timestamp: 'Today',
    },
    {
      id: 'a2',
      description: 'New content uploaded to "Launch Campaign"',
      context: 'Acme Marketing',
      timestamp: 'Yesterday',
    },
    {
      id: 'a3',
      description: 'You joined "Content Review" team',
      context: 'Tier One Studios',
      timestamp: '2 days ago',
    },
  ];

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
        py: 4,
      }}
    >
      <Container
        maxWidth="lg"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
        }}
      >
        {/* Page header */}
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }} gutterBottom>
            Dashboard (Sample Design)
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            A calmer, more focused layout for your organizations, teams, and content activity.
          </Typography>
        </Box>

        {/* Summary stats */}
        <Grid container spacing={2}>
          {stats.map((stat) => (
            <Grid key={stat.label} xs={12} sm={6} md={3}>
              <StatCard label={stat.label} value={stat.value} helper={stat.helper} />
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={3}>
          {/* Main column */}
          <Grid xs={12} md={8}>
            {/* Organizations section */}
            <Card
              sx={{
                mb: 3,
                borderRadius: 2,
                borderColor: 'divider',
                boxShadow: 'none',
                '&:hover': { transform: 'none', boxShadow: 'none' },
              }}
            >
              <CardHeader
                avatar={<Business sx={{ color: theme.palette.primary.main }} />}
                title="Your Organizations"
                subheader="Switch between workspaces and manage teams"
                action={
                  <Button
                    component={Link}
                    to="/organizations/new"
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon />}
                  >
                    New organization
                  </Button>
                }
              />
              <Divider />
              <CardContent sx={{ pt: 1 }}>
                {organizations.length === 0 ? (
                  <EmptyState
                    icon={<Business sx={{ fontSize: 40 }} />}
                    title="No organizations yet"
                    description="Create your first organization to start collaborating with teams."
                    actionLabel="Create organization"
                    actionTo="/organizations/new"
                  />
                ) : (
                  <List>
                    {organizations.map((org, index) => (
                      <React.Fragment key={org.id}>
                        <ListItem
                          component={Link}
                          to={`/organizations/${org.id}`}
                          sx={{
                            textDecoration: 'none',
                            color: 'inherit',
                            borderRadius: 1,
                            '&:hover': { bgcolor: 'action.hover' },
                          }}
                        >
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                                  {org.name}
                                </Typography>
                                <Chip
                                  label={org.role}
                                  size="small"
                                  variant="outlined"
                                  sx={{ height: 20, fontSize: '0.7rem' }}
                                />
                              </Box>
                            }
                            secondary={
                              <Typography variant="body2" color="text.secondary">
                                {org.teams} team{org.teams !== 1 ? 's' : ''}
                              </Typography>
                            }
                          />
                          <ListItemSecondaryAction>
                            <Button
                              component={Link}
                              to={`/organizations/${org.id}`}
                              size="small"
                              endIcon={<ArrowForward />}
                            >
                              Open
                            </Button>
                          </ListItemSecondaryAction>
                        </ListItem>
                        {index < organizations.length - 1 && <Divider component="li" />}
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>

            {/* Teams section */}
            <Card
              sx={{
                borderRadius: 2,
                borderColor: 'divider',
                boxShadow: 'none',
                '&:hover': { transform: 'none', boxShadow: 'none' },
              }}
            >
              <CardHeader
                avatar={<Group sx={{ color: theme.palette.secondary.main }} />}
                title="Your Teams"
                subheader="Jump back into the workspaces you use most"
              />
              <Divider />
              <CardContent sx={{ pt: 1 }}>
                {teams.length === 0 ? (
                  <EmptyState
                    icon={<Group sx={{ fontSize: 40 }} />}
                    title="No teams yet"
                    description="Join a team or create one within an organization to start collaborating."
                  />
                ) : (
                  <List>
                    {teams.map((team, index) => (
                      <React.Fragment key={team.id}>
                        <ListItem
                          component={Link}
                          to="/content"
                          sx={{
                            textDecoration: 'none',
                            color: 'inherit',
                            borderRadius: 1,
                            '&:hover': { bgcolor: 'action.hover' },
                          }}
                        >
                          <ListItemText
                            primary={
                              <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                                {team.name}
                              </Typography>
                            }
                            secondary={
                              <Typography variant="body2" color="text.secondary">
                                {team.organization}
                              </Typography>
                            }
                          />
                          <ListItemSecondaryAction>
                            <Button
                              component={Link}
                              to="/content"
                              size="small"
                              endIcon={<ArrowForward />}
                            >
                              Open
                            </Button>
                          </ListItemSecondaryAction>
                        </ListItem>
                        {index < teams.length - 1 && <Divider component="li" />}
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Sidebar */}
          <Grid xs={12} md={4}>
            {/* Quick actions */}
            <Card
              sx={{
                mb: 3,
                borderRadius: 2,
                borderColor: 'divider',
                boxShadow: 'none',
                '&:hover': { transform: 'none', boxShadow: 'none' },
              }}
            >
              <CardHeader
                title="Quick actions"
                subheader="Common tasks you might want to do now"
              />
              <Divider />
              <CardContent sx={{ pt: 2 }}>
                <Stack spacing={1.5}>
                  <Button
                    component={Link}
                    to="/content/upload"
                    variant="contained"
                    startIcon={<Description />}
                    fullWidth
                    sx={{ justifyContent: 'flex-start' }}
                  >
                    Upload new content
                  </Button>
                  <Button
                    component={Link}
                    to="/views"
                    variant="outlined"
                    fullWidth
                    sx={{ justifyContent: 'flex-start' }}
                  >
                    Browse saved views
                  </Button>
                  <Button
                    component={Link}
                    to="/subscriptions"
                    variant="outlined"
                    fullWidth
                    sx={{ justifyContent: 'flex-start' }}
                  >
                    Manage subscription
                  </Button>
                </Stack>
              </CardContent>
            </Card>

            {/* Recent activity */}
            <Card
              sx={{
                borderRadius: 2,
                borderColor: 'divider',
                boxShadow: 'none',
                '&:hover': { transform: 'none', boxShadow: 'none' },
              }}
            >
              <CardHeader
                avatar={<Timeline sx={{ color: theme.palette.info.main }} />}
                title="Recent activity"
                subheader="A quick snapshot across your workspaces"
              />
              <Divider />
              <CardContent sx={{ pt: 1 }}>
                {recentActivity.length === 0 ? (
                  <EmptyState
                    icon={<Timeline sx={{ fontSize: 40 }} />}
                    title="No recent activity"
                    description="When your teams start sharing and updating content, you’ll see it here."
                  />
                ) : (
                  <List dense>
                    {recentActivity.map((activity, index) => (
                      <React.Fragment key={activity.id}>
                        <ListItem sx={{ px: 0 }}>
                          <ListItemText
                            primary={
                              <Typography variant="body2" color="text.primary">
                                {activity.description}
                              </Typography>
                            }
                            secondary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                <Typography variant="caption" color="text.secondary">
                                  {activity.timestamp}
                                </Typography>
                                <Chip
                                  label={activity.context}
                                  size="small"
                                  variant="outlined"
                                  sx={{ height: 18, fontSize: '0.65rem' }}
                                />
                              </Box>
                            }
                          />
                        </ListItem>
                        {index < recentActivity.length - 1 && <Divider component="li" />}
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default DashboardSample;
